import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Download, FileSpreadsheet, FileText, Pencil, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';
import { MissionForm, toMissionPayload, type MissionFormData } from '@/components/missions/MissionForm';
import { RotationsTab } from '@/components/missions/RotationsTab';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MISSION_STATUS_LABELS, MISSION_STATUS_VARIANT, SHIFT_LABELS } from '@/lib/missions';

import type { Mission } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/missions/$id')({ component: MissionDetailPage });

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

// Fichier servi directement (pas d'URL présignée) : on passe par l'API pour porter le jeton, puis lien temporaire
const downloadExport = async (missionId: string, format: 'pdf' | 'excel', filename: string) => {
  try {
    const { data } = await api.get<Blob>(`/missions/${missionId}/export/${format}`, { responseType: 'blob' });
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    toast.error(apiErrorMessage(err, 'Export indisponible'));
  }
};

function MissionDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const canWrite = useHasPermission('missions.write');
  const canRead = useHasPermission('missions.read');
  const [editing, setEditing] = useState(false);

  const { data: mission, isLoading } = useQuery({
    queryKey: ['mission', id],
    queryFn: async () => (await api.get<Mission>(`/missions/${id}`)).data,
  });

  const update = useMutation({
    mutationFn: (d: MissionFormData) =>
      api.patch(`/missions/${id}`, toMissionPayload(d, mission?.status === 'IN_PROGRESS' ? 'locked' : 'update')),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mission', id] });
      void qc.invalidateQueries({ queryKey: ['missions'] });
      toast.success('Mission modifiée');
      setEditing(false);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de la modification')),
  });

  if (isLoading) return <div className="p-6"><Skeleton className="h-48 w-full" /></div>;
  if (!mission) return <div className="p-6 text-muted-foreground">Mission introuvable</div>;

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/missions"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Link>
        </Button>
        <h1 className="text-2xl font-bold font-mono">{mission.reference}</h1>
        <Badge variant={MISSION_STATUS_VARIANT[mission.status]}>{MISSION_STATUS_LABELS[mission.status] ?? mission.status}</Badge>
        <div className="ml-auto flex items-center gap-2">
          {canRead && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1" /> Exporter</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => void downloadExport(id, 'pdf', `bordereau-${mission.reference}.pdf`)}>
                  <FileText className="h-4 w-4 mr-2" /> Bordereau PDF
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void downloadExport(id, 'excel', `mission-${mission.reference}.xlsx`)}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" /> Classeur Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canWrite && !editing && ['DRAFT', 'SCHEDULED', 'IN_PROGRESS'].includes(mission.status) && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4 mr-1" /> Modifier
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Détails</TabsTrigger>
          <TabsTrigger value="rotations">Rotations ({mission.rotations?.length ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
        {editing ? (
          <MissionForm
            initial={mission}
            submitLabel="Enregistrer"
            pending={update.isPending}
            onSubmit={(d) => update.mutate(d)}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>Détails</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <Field label="Client" value={mission.client?.name ?? '—'} />
                {mission.primeContractor && <Field label="Donneur d’ordre final" value={mission.primeContractor.name} />}
                <Field label="Date prévue" value={fmtDate(mission.scheduledAt)} />
                <Field label="Origine" value={mission.originSite?.name ?? '—'} />
                <Field label="Destination" value={mission.destSite?.name ?? '—'} />
                <Field label="Distance estimée" value={mission.estimatedKm != null ? `${Number(mission.estimatedKm).toFixed(1)} km` : '—'} />
                {mission.startedAt && <Field label="Démarrée le" value={fmtDate(mission.startedAt)} />}
                {mission.completedAt && <Field label="Terminée le" value={fmtDate(mission.completedAt)} />}
                <Field
                  label="Shift"
                  value={mission.shift ? `${SHIFT_LABELS[mission.shift]} — ${fmtDate(mission.shiftStartAt)} → ${fmtDate(mission.shiftEndAt)}` : '—'}
                />
                <Field label="Objectif de rotations" value={mission.targetRotations != null ? String(mission.targetRotations) : '—'} />
              </CardContent>
            </Card>
            <div className="space-y-4">
              <Card>
                <CardHeader><CardTitle>Affectation</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <Field label="Véhicule" value={mission.vehicle?.plate ?? 'Non affecté'} />
                  <Field
                    label="Conducteur"
                    value={mission.driver ? `${mission.driver.firstName} ${mission.driver.lastName}` : 'Non affecté'}
                  />
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Cargaison</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <Field label="Description" value={mission.cargoDesc ?? '—'} />
                  <Field label="Poids" value={mission.cargoWeightTons != null ? `${mission.cargoWeightTons} t` : '—'} />
                  {mission.notes && <Field label="Notes" value={mission.notes} />}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        <FieldReports missionId={id} />
        </TabsContent>

        <TabsContent value="rotations">
          <RotationsTab mission={mission} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Réponse de GET /missions/:id/field-reports (saisies de l'app chauffeur)
interface FieldReportsResponse {
  checklists: { id: string; allOk: boolean; completedAt: string; items: { key: string; ok: boolean }[] }[];
  loadings: { id: string; qty: number; unit: string; signedByName: string | null; recordedAt: string; photoUrl: string | null }[];
  deliveries: {
    id: string; qty: number; unit: string; receiverName: string | null; receiverPhone: string | null;
    notes: string | null; recordedAt: string; photoUrl: string | null; signatureUrl: string | null;
  }[];
  incidents: { id: string; type: string; description: string; reportedAt: string; photoUrl: string | null }[];
}

const CHECKLIST_LABELS: Record<string, string> = {
  tires: 'Pneus', lights: 'Feux', brakes: 'Freins', fluids: 'Niveaux', battery: 'Batterie',
  mirrors: 'Rétroviseurs', wipers: 'Essuie-glaces', seatbelt: 'Ceinture', extinguisher: 'Extincteur',
  triangle: 'Triangle', kit: 'Trousse de secours', papers: 'Papiers du véhicule', license: 'Permis',
  fuel: 'Carburant', load_secured: 'Chargement arrimé',
};

const INCIDENT_LABELS: Record<string, string> = {
  BREAKDOWN: 'Panne', ACCIDENT: 'Accident', THEFT: 'Vol', OTHER: 'Autre',
};

const PhotoLink = ({ url, label }: { url: string | null; label: string }) =>
  url ? <a href={url} target="_blank" rel="noreferrer" className="text-primary-500 underline text-xs">{label}</a> : null;

function FieldReports({ missionId }: { missionId: string }) {
  const { data } = useQuery({
    queryKey: ['mission', missionId, 'field-reports'],
    queryFn: async () => (await api.get<FieldReportsResponse>(`/missions/${missionId}/field-reports`)).data,
  });
  if (!data) return null;
  const empty = !data.checklists.length && !data.loadings.length && !data.deliveries.length && !data.incidents.length;

  return (
    <Card>
      <CardHeader><CardTitle>Saisies terrain du conducteur</CardTitle></CardHeader>
      <CardContent className="space-y-4 text-sm">
        {empty && <p className="text-muted-foreground">Aucune saisie pour le moment.</p>}

        {data.checklists.map((c) => (
          <div key={c.id}>
            <div className="flex items-center gap-2 font-medium">
              Checklist HSE — {fmtDate(c.completedAt)}
              <Badge variant={c.allOk ? 'success' : 'warning'}>{c.allOk ? 'Conforme' : 'Anomalies'}</Badge>
            </div>
            <ul className="mt-1 grid grid-cols-3 gap-x-4 gap-y-0.5 text-xs">
              {c.items.map((i) => (
                <li key={i.key} className="flex items-center gap-1">
                  {i.ok ? <CheckCircle2 className="h-3 w-3 text-success" /> : <XCircle className="h-3 w-3 text-danger" />}
                  {CHECKLIST_LABELS[i.key] ?? i.key}
                </li>
              ))}
            </ul>
          </div>
        ))}

        {data.loadings.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center gap-x-3">
            <span className="font-medium">Chargement — {l.qty} {l.unit}</span>
            <span className="text-muted-foreground">{fmtDate(l.recordedAt)}</span>
            {l.signedByName && <span>Signé par {l.signedByName}</span>}
            <PhotoLink url={l.photoUrl} label="Photo" />
          </div>
        ))}

        {data.deliveries.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center gap-x-3">
            <span className="font-medium">Livraison — {d.qty} {d.unit}</span>
            <span className="text-muted-foreground">{fmtDate(d.recordedAt)}</span>
            {d.receiverName && <span>Reçu par {d.receiverName}{d.receiverPhone ? ` (${d.receiverPhone})` : ''}</span>}
            <PhotoLink url={d.photoUrl} label="Photo" />
            <PhotoLink url={d.signatureUrl} label="Signature" />
            {d.notes && <p className="w-full text-muted-foreground">{d.notes}</p>}
          </div>
        ))}

        {data.incidents.map((i) => (
          <div key={i.id} className="rounded border border-danger/40 bg-danger/5 p-2">
            <div className="flex flex-wrap items-center gap-x-3">
              <Badge variant="destructive">{INCIDENT_LABELS[i.type] ?? i.type}</Badge>
              <span className="text-muted-foreground">{fmtDate(i.reportedAt)}</span>
              <PhotoLink url={i.photoUrl} label="Photo" />
            </div>
            <p className="mt-1 whitespace-pre-wrap">{i.description}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const Field = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-muted-foreground text-xs uppercase tracking-wide">{label}</dt>
    <dd className="font-medium mt-0.5 whitespace-pre-wrap">{value}</dd>
  </div>
);
